import _ from "lodash"
import { GeolocationFeature, IFeature } from "interfaces"
import { AmenityData, OccupantData, UnitData } from "providers/venue/types"
import {
  getFeatureLevel,
  getFeatureName,
  getFeatureOrdinal,
  parseLevelText,
} from "./feature"

export type FindNearestDuplexLocationByOrigin = (
  origin: AmenityData | OccupantData | GeolocationFeature,
  destination: AmenityData | OccupantData | GeolocationFeature
) => AmenityData | OccupantData | GeolocationFeature

export type NormalizedRouteLocationResult =
  | AmenityData
  | OccupantData
  | GeolocationFeature

export type NormalizeRouteLocation = (
  location: AmenityData | OccupantData | UnitData | GeolocationFeature,
  routeLocation: AmenityData | OccupantData | UnitData | GeolocationFeature
) => NormalizedRouteLocationResult

export const transformGTMDirectionData = (feature: IFeature) => {
  const {
    name,
    category,
    local_categories = [],
  } = _.pick(feature?.properties, ["name", "category", "local_categories"])
  const id = _.get(feature, "id")
  const pickedOrigin = _.pick(feature, ["id", "feature_type"])
  const localCategoryNames = local_categories
    ?.map((taxonomy) => _.get(taxonomy, "properties.name.en"))
    .join(", ")

  return {
    ...pickedOrigin,
    category,
    id,
    name: _.get(name, "en"),
    local_categories: localCategoryNames,
  }
}

export const findNearestDuplexLocationByOrigin: FindNearestDuplexLocationByOrigin =
  (origin, destination) => {
    if (_.isNil(origin) || _.isNil(destination)) return
    const destFeatureType = destination?.feature_type
    let nearestFeature = null
    switch (destFeatureType) {
      case "occupant":
        const originOrdinal = _.get(origin, "properties.ordinal")

        if (!originOrdinal) break

        const availableDestinations = _.compact([
          destination,
          ..._.get(destination, "properties.units", []),
        ])
        nearestFeature = _.minBy(availableDestinations, (feature) =>
          Math.abs(feature.properties.ordinal - originOrdinal)
        )
        break
      default:
        break
    }

    return nearestFeature || destination
  }

// Preserve name, id, and ordinal from the original route to:
// - Ensure correct UI display
// - Allow switching between origin and destination using the same data
export const normalizeRouteLocation: NormalizeRouteLocation = (
  location,
  routeLocation
) => {
  return {
    ...location,
    properties: {
      ...(location?.properties || {}),
      // TODO: Find a better solution for determining which location ID to highlight as the destination on the step page.
      //@ts-ignore
      target_location_id:
        routeLocation?.id !== location?.id ? routeLocation?.id : location?.id,
      level: getFeatureLevel(routeLocation),
      level_name: parseLevelText(routeLocation),
      name: getFeatureName(location),
      ordinal: getFeatureOrdinal(routeLocation),
    },
  } as NormalizedRouteLocationResult
}
